import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Search, RefreshCw, ArrowUpDown, Loader2, Copy, Package } from 'lucide-react';
import { adminGetAllProducts, adminDeleteProduct, adminDuplicateProduct } from '../../../services/admin/shopAdminService';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { Product as ProductType } from '../../../services/shopService';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription, DialogClose
} from '../../../components/ui/dialog';
import { useToast } from '../../../hooks/useToast';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { useDomainQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';

type Product = ProductType & {
  category_name?: string;
};

const AdminProductList: React.FC = () => {
  useCheckPermissions(['admin']);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Utiliser la logique de domaine
  const { getFilterParams } = useDomainQuery();
  const { domain } = getFilterParams();
  
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [categories, setCategories] = useState<{id: number, name: string}[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [isDuplicating, setIsDuplicating] = useState<number | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: '/admin/boutique' } });
    }
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Récupérer les produits et les catégories du domaine actuel
      const [productsData, categoriesData] = await Promise.all([
        adminGetAllProducts(domain || ''),
        supabase.from('shop_categories').select('id, name').order('name')
      ]);

      if (categoriesData.error) throw categoriesData.error;

      console.log('Produits chargés:', productsData); // Ajout du log pour déboguer
      setProducts(productsData);
      setCategories(categoriesData.data || []);
    } catch (err) {
      setError("Erreur lors du chargement des données");
      toast({ title: 'Erreur', description: 'Impossible de charger les données.', variant: 'destructive' });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredProducts = products
    .filter(product => 
      (searchTerm === '' || product.title.toLowerCase().includes(searchTerm.toLowerCase())) &&
      (selectedCategory === 'all' || product.category_id === Number(selectedCategory))
    )
    .sort((a, b) => {
      const fieldA = a[sortField as keyof Product] as any;
      const fieldB = b[sortField as keyof Product] as any;

      if (fieldA === null || fieldA === undefined) return 1;
      if (fieldB === null || fieldB === undefined) return -1;

      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc' ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
      }
      if (typeof fieldA === 'number' && typeof fieldB === 'number') {
        return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
      }
      return sortDirection === 'asc'
        ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const handleDelete = async () => {
    if (!productToDelete) return;
    try {
      await adminDeleteProduct(productToDelete.id);
      toast({ title: 'Succès', description: `Le produit "${productToDelete.title}" a été supprimé.` });
      setProductToDelete(null);
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer le produit.', variant: 'destructive' });
    }
  };

  const handleDuplicate = async (productId: number) => {
    try {
      setIsDuplicating(productId);
      const newProduct = await adminDuplicateProduct(productId, domain || undefined);
      toast({ 
        title: 'Succès', 
        description: `Le produit a été dupliqué avec succès !`
      });
      loadData();
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      toast({ 
        title: 'Erreur', 
        description: 'Une erreur est survenue lors de la duplication du produit.', 
        variant: 'destructive' 
      });
    } finally {
      setIsDuplicating(null);
    }
  };

  if (loading) {
    return <AdminLayout><div className="flex justify-center items-center h-full py-32"><Loader2 className="h-12 w-12 animate-spin text-[#D4AF37]" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* En-tête */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Package className="h-8 w-8 text-[#D4AF37]" />
              Gestion des Produits
            </h1>
            <p className="text-gray-400 mt-2">
              {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''} trouvé{filteredProducts.length > 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => loadData()} variant="outline" size="sm" className="border-white/10 text-white hover:bg-white/5">
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            <Button asChild className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] text-white hover:opacity-90">
              <Link to="/admin/boutique/creer">
                <Plus size={18} className="mr-2" />
                Nouveau produit
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Total</div>
            <div className="text-2xl font-bold text-white">{products.length}</div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">En stock</div>
            <div className="text-2xl font-bold text-green-400">
              {products.filter(p => p.stock_quantity && p.stock_quantity > 0).length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Stock faible</div>
            <div className="text-2xl font-bold text-yellow-400">
              {products.filter(p => p.stock_quantity && p.stock_quantity > 0 && p.stock_quantity <= 5).length}
            </div>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-sm text-gray-400 mb-1">Épuisés</div>
            <div className="text-2xl font-bold text-red-400">
              {products.filter(p => p.stock_quantity === 0).length}
            </div>
          </div>
        </div>
        {/* Filtres et recherche */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                type="text"
                placeholder="Rechercher par nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400"
              />
            </div>

            {/* Filtre Catégorie */}
            <div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:ring-2 focus:ring-[#D4AF37]/50"
              >
                <option value="all" className="bg-gray-900">Toutes catégories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id} className="bg-gray-900">{cat.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {error && <p className="text-red-400 text-center py-8">{error}</p>}

        {/* Table des produits */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Aucun produit trouvé</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('title')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Nom
                        <ArrowUpDown size={14} className={sortField === 'title' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Catégorie
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('price')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Prix
                        <ArrowUpDown size={14} className={sortField === 'price' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button
                        onClick={() => handleSort('stock_quantity')}
                        className="flex items-center gap-2 hover:text-white transition-colors"
                      >
                        Stock
                        <ArrowUpDown size={14} className={sortField === 'stock_quantity' ? 'text-[#D4AF37]' : ''} />
                      </button>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredProducts.map((product, index) => (
                    <tr
                      key={product.id}
                      className={`hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.images && product.images.length > 0 ? (
                          <img 
                            src={product.images[0].image_url} 
                            alt={product.title}
                            className="h-10 w-10 object-cover rounded-lg"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = '/placeholder.svg';
                            }}
                          />
                        ) : (
                          <div className="h-10 w-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                            <Package className="h-5 w-5 text-[#D4AF37]" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-white">{product.title}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{product.category_name || categories.find(c => c.id === product.category_id)?.name || '-'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{product.price ? `${new Intl.NumberFormat('fr-FR').format(product.price)} FCFA` : 'Sur demande'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {product.stock_quantity === null ? (
                          <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">N/A</Badge>
                        ) : product.stock_quantity > 5 ? (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">En stock ({product.stock_quantity})</Badge>
                        ) : product.stock_quantity > 0 ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Faible ({product.stock_quantity})</Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Épuisé</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className={product.is_unique ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}>
                          {product.is_unique ? 'Pièce unique' : 'Édition limitée'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => navigate(`/admin/boutique/edit/${product.id}`)}
                            className="text-[#D4AF37] hover:text-[#B8941F]"
                          >
                            <Edit size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicate(product.id)}
                            disabled={isDuplicating === product.id}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            {isDuplicating === product.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Copy size={18} />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setProductToDelete(product)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      <Dialog open={productToDelete !== null} onOpenChange={(open) => !open && setProductToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le produit "{productToDelete?.title}" ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" onClick={() => setProductToDelete(null)}>Annuler</Button>
            </DialogClose>
            <Button variant="destructive" onClick={handleDelete}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminProductList;
